import React from "react";

// Minimal markdown for the Executive journal: #/## headings, - bullets,
// - [ ] / - [x] checkboxes, **bold**, *italic*. No external deps.

function inline(text, keyBase) {
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] != null) nodes.push(<strong key={`${keyBase}-b${i}`}>{m[2]}</strong>);
    else nodes.push(<em key={`${keyBase}-i${i}`}>{m[3]}</em>);
    last = m.index + m[0].length; i += 1;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function MarkdownView({ text, className = "" }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ t: "ul", items: list }); list = null; } };
  lines.forEach((raw) => {
    const line = raw.replace(/\s+$/, "");
    let mm;
    if (/^#\s+/.test(line)) { flush(); blocks.push({ t: "h1", text: line.replace(/^#\s+/, "") }); }
    else if (/^##\s+/.test(line)) { flush(); blocks.push({ t: "h2", text: line.replace(/^##\s+/, "") }); }
    else if ((mm = line.match(/^\s*-\s*\[([ xX])\]\s+(.*)$/))) { (list = list || []).push({ check: true, checked: mm[1].toLowerCase() === "x", text: mm[2] }); }
    else if (/^\s*[-*]\s+/.test(line)) { (list = list || []).push({ check: false, text: line.replace(/^\s*[-*]\s+/, "") }); }
    else if (line.trim() === "") { flush(); blocks.push({ t: "sp" }); }
    else { flush(); blocks.push({ t: "p", text: line }); }
  });
  flush();

  return (
    <div className={`text-sm leading-relaxed text-gray-800 ${className}`}>
      {blocks.map((b, i) => {
        if (b.t === "h1") return <h1 key={i} className="mt-3 mb-1 font-display text-xl font-bold text-gray-900">{inline(b.text, `h1${i}`)}</h1>;
        if (b.t === "h2") return <h2 key={i} className="mt-3 mb-1 font-display text-base font-bold text-gray-900">{inline(b.text, `h2${i}`)}</h2>;
        if (b.t === "sp") return <div key={i} className="h-2" />;
        if (b.t === "ul") return (
          <ul key={i} className="my-1 space-y-1">
            {b.items.map((it, j) => it.check ? (
              <li key={j} className="flex items-start gap-2">
                <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-[3px] border text-[10px] ${it.checked ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300 text-transparent"}`}>✓</span>
                <span className={it.checked ? "text-gray-400 line-through" : ""}>{inline(it.text, `c${i}${j}`)}</span>
              </li>
            ) : (
              <li key={j} className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" /><span>{inline(it.text, `l${i}${j}`)}</span></li>
            ))}
          </ul>
        );
        return <p key={i} className="my-1">{inline(b.text, `p${i}`)}</p>;
      })}
    </div>
  );
}

// Strip markdown markers for compact list previews / titles.
export function stripMarkdown(text) {
  return (text || "")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*-\s*\[[ xX]\]\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .trim();
}
